import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Home, ArrowLeft, Search } from 'lucide-react';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { trackPageNotFound } from '@/utils/Analytics';

const NotFound: React.FC = () => {
  const location = useLocation();
  const { currentUser } = useEnhancedAuth();
  const isAuthenticated = !!currentUser;

  useEffect(() => {
    // Track 404 page visit
    trackPageNotFound(
      location.pathname + location.search,
      document.referrer,
      isAuthenticated
    );
  }, [location.pathname, location.search, isAuthenticated]);

  const suggestedPages = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'Dashboard', path: '/dashboard', icon: Search, requireAuth: true },
    { name: 'About Us', path: '/about', icon: Search },
    { name: 'Pricing', path: '/pricing', icon: Search },
  ];

  const filteredSuggestions = suggestedPages.filter(page => 
    !page.requireAuth || isAuthenticated
  );

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Page Not Found - Social Work Test Prep Academy</title>
        <meta name="description" content="The page you're looking for doesn't exist. Return to Social Work Test Prep Academy." />
        <meta name="robots" content="noindex, nofollow" />
        {/* Clean canonical from the pathname only — never window.location.href,
            which would fold UTM/ref query strings into the canonical. */}
        <link
          rel="canonical"
          href={`https://www.socialworktestprepacademy.com${location.pathname}`}
        />
      </Helmet>

      <div className="max-w-md w-full text-center">
        {/* 404 Illustration */}
        <div className="mb-8 animate-fade-in-up opacity-0">
          <div className="text-6xl font-bold text-neutral-300 mb-4">404</div>
          <div className="w-32 h-32 mx-auto mb-4 bg-primary-100 rounded-full flex items-center justify-center">
            <Search className="w-16 h-16 text-primary-500" />
          </div>
        </div>

        {/* Error Message */}
        <div className="animate-fade-in-up opacity-0 delay-100">
          <h1 className="heading-1 mb-4">
            Page Not Found
          </h1>
          <p className="body-base text-neutral-600 mb-8">
            Sorry, we couldn't find the page you're looking for. The URL might be mistyped or the page may have been moved.
          </p>
        </div>

        {/* Requested URL for debugging */}
        {location.pathname !== '/404' && (
          <div className="card-soft bg-neutral-100 mb-6 animate-fade-in-up opacity-0 delay-100">
            <p className="text-sm text-neutral-500">
              Requested: <code className="text-error">{location.pathname}</code>
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-4 mb-8 animate-fade-in-up opacity-0 delay-200">
          <button
            onClick={() => window.history.back()}
            className="w-full flex items-center justify-center px-4 py-3 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-100 transition-all"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </button>

          <Link
            to={isAuthenticated ? "/dashboard" : "/"}
            className="btn-primary w-full"
          >
            <Home className="w-4 h-4" />
            Return Home
          </Link>
        </div>

        {/* Suggested Pages */}
        <div className="text-left animate-fade-in-up opacity-0 delay-300">
          <h2 className="text-base font-semibold text-neutral-900 mb-4">
            Suggested Pages
          </h2>
          <div className="space-y-2">
            {filteredSuggestions.map((page) => {
              const Icon = page.icon;
              return (
                <Link
                  key={page.path}
                  to={page.path}
                  className="flex items-center p-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 hover:border-primary-300 transition-all"
                >
                  <Icon className="w-4 h-4 mr-3 text-neutral-400" />
                  <span className="text-sm text-neutral-700">{page.name}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Support Contact */}
        <div className="mt-8 pt-6 border-t border-neutral-200 animate-fade-in-up opacity-0 delay-400">
          <p className="text-sm text-neutral-500">
            Still having trouble?{' '}
            <a
              href="mailto:support@socialworktestprepacademy.com"
              className="text-primary-600 hover:text-primary-700 hover:underline"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;